import type { TowerType } from "@mmotdg/shared";
import type * as THREE from "three";
import { defaultProjectileSkins } from "./default.js";
import type { ProjectileBuildOptions, ProjectileModelView, ProjectileSkin } from "./types.js";

export { PROJECTILE_HEIGHT } from "./core.js";
export type { ProjectileBuildOptions, ProjectileModelView, ProjectileSkin };

const SKINS: Record<TowerType, Record<string, ProjectileSkin>> = {
  archer: { default: defaultProjectileSkins.archer },
  cannon: { default: defaultProjectileSkins.cannon },
  frost: { default: defaultProjectileSkins.frost },
  lightning: { default: defaultProjectileSkins.lightning },
  poison: { default: defaultProjectileSkins.poison },
  support: { default: defaultProjectileSkins.support },
  antiair: { default: defaultProjectileSkins.antiair },
  economy: { default: defaultProjectileSkins.economy },
};

const EQUIPPED_STORAGE_KEY = "mmotdg.equippedProjectileSkins";
const equipped: Partial<Record<TowerType, string>> = loadEquippedFromStorage();

function loadEquippedFromStorage(): Partial<Record<TowerType, string>> {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(EQUIPPED_STORAGE_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistEquipped(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(EQUIPPED_STORAGE_KEY, JSON.stringify(equipped));
  } catch {
    /* ignore */
  }
}

export function registerProjectileSkin(skin: ProjectileSkin): void {
  if (!SKINS[skin.towerType]) SKINS[skin.towerType] = {};
  SKINS[skin.towerType][skin.id] = skin;
}

export function getProjectileSkin(type: TowerType, skinId?: string): ProjectileSkin {
  const bucket = SKINS[type];
  const id = skinId ?? equipped[type] ?? "default";
  return bucket[id] ?? bucket.default;
}

export function listProjectileSkins(type: TowerType): ProjectileSkin[] {
  return Object.values(SKINS[type] ?? {});
}

export function getEquippedProjectileSkinId(type: TowerType): string {
  return equipped[type] ?? "default";
}

export function equipProjectileSkin(type: TowerType, skinId: string): void {
  if (skinId === "default") {
    delete equipped[type];
  } else {
    equipped[type] = skinId;
  }
  persistEquipped();
}

export function buildProjectileModel(type: TowerType, options: ProjectileBuildOptions = {}): ProjectileModelView {
  const skin = getProjectileSkin(type, options.skinId);
  return skin.build();
}

export function disposeProjectileModel(view: ProjectileModelView): void {
  view.mesh.geometry.dispose();
  (view.mesh.material as THREE.Material).dispose();
}
